/**
 * Contratos alinhados à API CreditOn — Monitoramento Serasa (Trombini).
 * @see MonitoramentoSerasaController / DTOs no backend.
 */

/** `SegmentoCarteiraMonitoramento` — ASP.NET serializa como número. */
export type SegmentoCarteiraMonitoramento = 0 | 1 | 2;

/** `TendenciaMonitoramento` */
export type TendenciaMonitoramento = 0 | 1 | 2 | 3;

/** `SeveridadeEventoMonitoramento` */
export type SeveridadeEventoMonitoramento = 0 | 1 | 2;

export interface MonitoramentoSerasaSnapshotDto {
  id?: number;
  dataConsulta?: string;
  cnpj?: string;
  razaoSocial?: string | null;
  totalPendencias?: number;
  valorTotalPefin?: number;
  quantidadePefin?: number;
  valorRefin?: number;
  quantidadeRefin?: number;
  quantidadeDividasVencidas?: number;
  valorDividasVencidas?: number;
  quantidadeProtestos?: number;
  valorProtestos?: number;
  quantidadeAcoesJudiciais?: number;
  valorAcoesJudiciais?: number;
  /**
   * Soma na API: `totalPendencias` + `quantidadePefin` + `quantidadeRefin` +
   * `quantidadeDividasVencidas` + `quantidadeProtestos` + `quantidadeAcoesJudiciais`
   * (coluna persistida ignorada na leitura).
   */
  quantidadeTotalAnotacoes?: number;
  /**
   * Soma na API: `valorTotalPefin` + `valorRefin` + `valorDividasVencidas` +
   * `valorProtestos` + `valorAcoesJudiciais` (coluna persistida ignorada na leitura).
   */
  valorTotalAnotacoes?: number;
  enviado?: boolean;
}

export interface MonitoramentoSerasaDeltaDto {
  valorTotalAnotacoes?: number;
  quantidadeTotalAnotacoes?: number;
  quantidadeProtestos?: number;
  valorProtestos?: number;
  quantidadeAcoesJudiciais?: number;
  valorAcoesJudiciais?: number;
}

export interface MonitoramentoSerasaLinhaCarteiraDto
  extends MonitoramentoSerasaSnapshotDto {
  segmentoCarteira?: SegmentoCarteiraMonitoramento | number;
  motivosSegmento?: string[];
  tendenciaVsAnterior?: TendenciaMonitoramento | number;
  scoreMonitoramento?: number;
  precisaRevisao?: boolean;
}

export interface MonitoramentoSerasaListaCarteiraRespostaDto {
  versaoRegras?: number;
  pagina?: number;
  tamanhoPagina?: number;
  totalRegistros?: number;
  totalPaginas?: number;
  itens?: MonitoramentoSerasaLinhaCarteiraDto[];
}

export interface MonitoramentoSerasaMudancaResumoDto {
  cnpj?: string;
  razaoSocial?: string;
  indicadorPrincipal?: string;
  variacaoValorTotalAnotacoes?: number;
  severidade?: SeveridadeEventoMonitoramento | number;
}

export interface MonitoramentoSerasaDigestDto {
  versaoRegras?: number;
  geradoEmUtc?: string;
  periodoAtualInicioUtc?: string;
  periodoAtualFimUtc?: string;
  periodoAnteriorInicioUtc?: string;
  periodoAnteriorFimUtc?: string;
  quantidadePioraram?: number;
  quantidadeMelhoraram?: number;
  quantidadeEstaveis?: number;
  topMudancasNegativas?: MonitoramentoSerasaMudancaResumoDto[];
  topMudancasPositivas?: MonitoramentoSerasaMudancaResumoDto[];
  insights?: string[];
}

export interface MonitoramentoSerasaResumoDto {
  versaoRegras?: number;
  geradoEmUtc?: string;
  totalCnpjsMonitorados?: number;
  somaValorTotalAnotacoes?: number;
  somaValorProtestos?: number;
  totalQuantidadeProtestos?: number;
  cnpjsComProtesto?: number;
  cnpjsComAcaoJudicial?: number;
  /** Chaves: nome do enum, ex. `"Critico"`, `"Atencao"`, `"Saudavel"`. */
  distribuicaoSegmento?: Record<string, number>;
}

/**
 * `tipoEvento` — string emitida pelo motor (exemplos):
 * `PrimeiroProtestoRegistrado`, `ProtestoAumentou`, `AcaoJudicialNova`,
 * `DividaVencidaSubiu`, `ValorTotalAnotacoesCruzouLimiarAtencao`,
 * `ValorTotalAnotacoesCruzouLimiarCritico`, `ValorTotalAnotacoesSubiu`.
 */
export interface MonitoramentoSerasaEventoDto {
  tipoEvento?: string;
  severidade?: SeveridadeEventoMonitoramento | number;
  cnpj?: string;
  razaoSocial?: string;
  dataConsulta?: string;
  mensagemCurta?: string;
  valoresDepois?: MonitoramentoSerasaSnapshotDto;
  valoresAntes?: MonitoramentoSerasaSnapshotDto;
}

export interface MonitoramentoSerasaEventosRespostaDto {
  versaoRegras?: number;
  total?: number;
  itens?: MonitoramentoSerasaEventoDto[];
}

export interface MonitoramentoSerasaExposicaoItemDto {
  cnpj?: string;
  razaoSocial?: string;
  valorTotalAnotacoes?: number;
  percentualDoTotalCarteira?: number;
}

export interface MonitoramentoSerasaExposicaoDto {
  versaoRegras?: number;
  geradoEmUtc?: string;
  totalValorAnotacoesCarteira?: number;
  top10PorValorAnotacoes?: MonitoramentoSerasaExposicaoItemDto[];
  percentualTop5Acumulado?: number;
  percentualTop10Acumulado?: number;
}

export interface MonitoramentoSerasaFrescorDto {
  versaoRegras?: number;
  geradoEmUtc?: string;
  ultimaDataConsultaGlobal?: string | null;
  totalCnpjs?: number;
  cnpjsDesatualizados?: number;
  diasLimiteDesatualizado?: number;
}

export interface MonitoramentoSerasaDistribuicaoDto {
  versaoRegras?: number;
  geradoEmUtc?: string;
  segmentos?: Record<string, number>;
}

/** Ponto do histórico: snapshot + campos de carteira + delta. */
export type MonitoramentoSerasaPontoHistoricoDto =
  MonitoramentoSerasaSnapshotDto &
    Partial<
      Pick<
        MonitoramentoSerasaLinhaCarteiraDto,
        | "segmentoCarteira"
        | "motivosSegmento"
        | "tendenciaVsAnterior"
        | "scoreMonitoramento"
        | "precisaRevisao"
      >
    > & {
      deltaEmRelacaoAnterior?: MonitoramentoSerasaDeltaDto;
    };

export interface MonitoramentoSerasaSerieCompactaDto {
  datas?: string[];
  valorTotalAnotacoes?: number[];
  /** Paralelo a `datas` / `valorTotalAnotacoes`. */
  quantidadeTotalAnotacoes?: number[];
  valorProtestos?: number[];
  quantidadeProtestos?: number[];
}

/**
 * Um item por mês-calendário: totais = retrato da **última consulta** daquele mês
 * (não soma várias consultas). `marcosDoMes` = só transição **consulta imediatamente
 * anterior → última consulta do mês** na linha do tempo.
 */
export interface MonitoramentoSerasaMesHistoricoDto {
  ano?: number;
  mes?: number;
  /** Ex.: `"2025-03"`. */
  anoMes?: string;
  ultimaConsultaNoMesUtc?: string;
  consultasNoMes?: number;
  quantidadeTotalAnotacoes?: number;
  valorTotalAnotacoes?: number;
  quantidadeProtestos?: number;
  valorProtestos?: number;
  quantidadeAcoesJudiciais?: number;
  valorAcoesJudiciais?: number;
  /** Estado completo da última consulta do mês (gráfico / tooltip). */
  snapshotUltimaConsultaNoMes?: MonitoramentoSerasaSnapshotDto;
  marcosDoMes?: MonitoramentoSerasaEventoDto[];
}

export interface MonitoramentoSerasaHistoricoRespostaDto {
  versaoRegras?: number;
  cnpj?: string;
  /** Primeira linha do CNPJ na base (ISO). */
  primeiraConsultaUtc?: string | null;
  pontos?: MonitoramentoSerasaPontoHistoricoDto[];
  marcos?: MonitoramentoSerasaEventoDto[];
  serieCompacta?: MonitoramentoSerasaSerieCompactaDto;
  /** Série mensal; `incluirSerieMensal=false` na API reduz payload. */
  serieMensal?: MonitoramentoSerasaMesHistoricoDto[];
  insights?: string[];
}

export interface MonitoramentoSerasaRelatorioSemanalDto {
  versaoRegras?: number;
  geradoEmUtc?: string;
  periodoInicioUtc?: string;
  periodoFimUtc?: string;
  quantidadeCnpjsComSnapshot?: number;
  urlArquivo?: string;
  nomeArquivo?: string;
}

/** Converte `serieCompacta` (arrays paralelos) em linhas para gráficos. */
export function serieCompactaParaLinhas(
  serie?: MonitoramentoSerasaSerieCompactaDto | null
): Array<{
  data: string;
  valorTotalAnotacoes: number;
  quantidadeTotalAnotacoes?: number;
  valorProtestos?: number;
}> {
  const datas = serie?.datas ?? [];
  if (datas.length === 0) return [];
  const valores = serie?.valorTotalAnotacoes ?? [];
  const qtdTot = serie?.quantidadeTotalAnotacoes ?? [];
  const protestos = serie?.valorProtestos ?? [];
  return datas.map((data, i) => ({
    data: data ?? "",
    valorTotalAnotacoes: valores[i] ?? 0,
    quantidadeTotalAnotacoes: qtdTot[i],
    valorProtestos: protestos[i],
  }));
}

/** Linhas para gráfico a partir de `serieMensal`. */
export function serieMensalParaLinhas(
  meses?: MonitoramentoSerasaMesHistoricoDto[] | null
): Array<{
  anoMes: string;
  valorTotalAnotacoes: number;
  quantidadeTotalAnotacoes?: number;
  consultasNoMes?: number;
  marcosNoMes: number;
  quantidadeProtestos?: number;
  valorProtestos?: number;
  quantidadeAcoesJudiciais?: number;
  valorAcoesJudiciais?: number;
  ultimaConsultaNoMesUtc?: string;
}> {
  if (!meses?.length) return [];
  return meses.map((m) => {
    const snap = m.snapshotUltimaConsultaNoMes;
    return {
      anoMes:
        m.anoMes ??
        (m.ano != null && m.mes != null
          ? `${m.ano}-${String(m.mes).padStart(2, "0")}`
          : "—"),
      valorTotalAnotacoes: m.valorTotalAnotacoes ?? snap?.valorTotalAnotacoes ?? 0,
      quantidadeTotalAnotacoes:
        m.quantidadeTotalAnotacoes ?? snap?.quantidadeTotalAnotacoes,
      consultasNoMes: m.consultasNoMes,
      marcosNoMes: m.marcosDoMes?.length ?? 0,
      quantidadeProtestos: m.quantidadeProtestos ?? snap?.quantidadeProtestos,
      valorProtestos: m.valorProtestos ?? snap?.valorProtestos,
      quantidadeAcoesJudiciais: m.quantidadeAcoesJudiciais ?? snap?.quantidadeAcoesJudiciais,
      valorAcoesJudiciais: m.valorAcoesJudiciais ?? snap?.valorAcoesJudiciais,
      ultimaConsultaNoMesUtc: m.ultimaConsultaNoMesUtc ?? snap?.dataConsulta,
    };
  });
}

/* --- Aliases usados pelos componentes (nomes curtos) --- */

export type MonitoramentoDigest = MonitoramentoSerasaDigestDto;
export type MonitoramentoResumo = MonitoramentoSerasaResumoDto;
export type PaginaCnpjs = MonitoramentoSerasaListaCarteiraRespostaDto;
export type EventoMonitoramento = MonitoramentoSerasaEventoDto;
export type PaginaEventos = MonitoramentoSerasaEventosRespostaDto;
export type HistoricoFornecedor = MonitoramentoSerasaHistoricoRespostaDto;
export type CarteiraExposicao = MonitoramentoSerasaExposicaoDto;
export type CarteiraFrescor = MonitoramentoSerasaFrescorDto;
export type RelatorioSemanalPayload = MonitoramentoSerasaRelatorioSemanalDto;
export type SnapshotSerasaLinha = MonitoramentoSerasaLinhaCarteiraDto;
